import { Phase } from '../core/game/phases.js';
import type { Screen, ScreenContext, ScreenFactory } from './router.js';
import { createBriefingScreen } from './briefing-screen.js';
import { createRewardScreen } from './reward-screen.js';
import { createGarageScreen } from './garage-screen.js';
import { createCollectionScreen } from './collection-screen.js';
import { createHubScreen } from './hub-screen.js';
import { createParentScreen } from './parent-screen.js';
import { createCelebrationScreen } from './celebration-screen.js';

/**
 * The screen table: one factory per phase.
 *
 * There is no scaffolding left here. Every phase has a real screen, and none of them contain a word or a
 * number - which was the point of the exercise: she cannot read, so any text on screen is either
 * decoration or a dead end.
 *
 * **The two big screens fetch their own code when they are needed, not at boot.** Between them the studio
 * and the drive leg pull in Planck and Pixi, and naming them up here put all of that in the one entry
 * chunk - 363kB of it, parsed on every cold launch before the hub, which draws nothing but DOM, could show
 * a single paw. The service worker means it is not a *download* after the first run, but parse and execute
 * are paid every time she opens the game, on the slowest device in the house. `preloadHeavyScreens` pulls
 * them in once the hub is up, so the wait never lands on a press.
 */

export const SCREENS: Readonly<Record<Phase, ScreenFactory>> = {
  [Phase.Hub]: () => createHubScreen(),
  [Phase.Garage]: () => createGarageScreen(),
  [Phase.Collection]: () => createCollectionScreen(),
  /*
   * The only screen here with words on it, and the only one she cannot reach by pressing something - a
   * three-second hold in the corner of the Lookout, then a sequence. It is in the entry chunk with the rest
   * of the menus: it is a few hundred lines of DOM, and the one thing it must never do is fail to open when
   * a parent has finally got through the lock.
   */
  [Phase.Parent]: () => createParentScreen(),
  [Phase.Draw]: onDemand(async () => (await import('../draw/draw-screen.js')).createDrawScreen()),
  [Phase.Briefing]: () => createBriefingScreen(),
  [Phase.DriveOut]: onDemand(loadDriveScreen),
  [Phase.Collected]: () => createCelebrationScreen('collected'),
  [Phase.DriveBack]: onDemand(loadDriveScreen),
  [Phase.Arrived]: () => createCelebrationScreen('arrived'),
  [Phase.Reward]: () => createRewardScreen(),
  [Phase.FreePlay]: onDemand(loadDriveScreen),
};

async function loadDriveScreen(): Promise<Screen> {
  return (await import('../drive/drive-screen.js')).createDriveScreen();
}

/**
 * Warm the chunks the heavy screens live in.
 *
 * Called once the hub has painted, so the fetch and the parse happen while she is deciding what to press
 * rather than after she has pressed it. Failures are swallowed on purpose: a warm-up that could not run
 * only costs a slower first press, and the same `import()` runs again when the screen is really wanted.
 */
export function preloadHeavyScreens(): void {
  void import('../draw/draw-screen.js').catch(() => undefined);
  void import('../drive/drive-screen.js').catch(() => undefined);
}

/**
 * A screen that fetches its own code on the way in.
 *
 * The router stays synchronous - it hands out a Screen and mounts it in the same breath - so the waiting
 * happens inside `mount`, and the root simply stays empty for the frame or two the import takes. Unmounting
 * before the code lands has to be safe: she can press her way back out the instant a drive starts loading,
 * and a screen that mounted itself after that would put a Pixi canvas over the top of whatever came next.
 */
function onDemand(load: () => Promise<Screen>): ScreenFactory {
  return () => {
    let inner: Screen | null = null;
    let live = true;

    return {
      mount(root: HTMLElement, ctx: ScreenContext): void {
        void load().then(
          (screen) => {
            if (!live) return;
            inner = screen;
            screen.mount(root, ctx);
          },
          (error: unknown) => {
            console.error('[pawp] screen failed to load', error);
          },
        );
      },
      unmount(): void {
        live = false;
        inner?.unmount?.();
        inner = null;
      },
    };
  };
}
